import { useState } from "react";
import { Box, Typography } from "@mui/material";

// Small interactive trend chart — line + area + dots + month axis + hover
// tooltip. Dependency-free. The HTML overlay (dots, tooltip, labels) is
// positioned with inline `style` (NOT `sx`) on purpose: community-proj runs
// stylis-plugin-rtl, which flips `left`/`transform` in emotion styles for RTL
// but cannot touch the SVG line — so using `sx` there desynced the dots/labels
// from the line in Hebrew. Inline styles are physical and stay in sync. The
// chart reads left-to-right in both directions.
// `points`: [{ value, label, full }]. `formatValue(value)` formats the tooltip.
const Sparkline = ({
  points = [],
  color = "rgba(36, 139, 112, 1)",
  fill = "rgba(36, 139, 112, 0.12)",
  height = 60,
  formatValue = (v) => v,
}) => {
  const [hover, setHover] = useState(null);

  if (!points || points.length < 2) return null;

  const values = points.map((p) => p.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const n = points.length;
  const padX = 4;
  const padY = 12;

  const coords = points.map((p, i) => {
    const x = padX + (i / (n - 1)) * (100 - padX * 2);
    const y = padY + (1 - (p.value - min) / range) * (100 - padY * 2);
    return { x, y, ...p };
  });

  const line = coords.map((c) => `${c.x.toFixed(2)},${c.y.toFixed(2)}`).join(" ");
  const area = `${coords[0].x.toFixed(2)},100 ${line} ${coords[n - 1].x.toFixed(2)},100`;

  return (
    <Box sx={{ width: "100%", mt: "10px" }}>
      <Box sx={{ position: "relative", width: "100%", height: `${height}px` }}>
        <svg
          viewBox="0 0 100 100"
          width="100%"
          height="100%"
          preserveAspectRatio="none"
          style={{ display: "block", overflow: "visible" }}
          aria-hidden="true"
        >
          <polygon points={area} fill={fill} />
          <polyline
            points={line}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {coords.map((c, i) => (
          <Box
            key={i}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            style={{
              position: "absolute",
              left: `${c.x}%`,
              top: `${c.y}%`,
              transform: "translate(-50%, -50%)",
            }}
            sx={{
              width: 18,
              height: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                width: hover === i ? 11 : 8,
                height: hover === i ? 11 : 8,
                borderRadius: "50%",
                backgroundColor: "#fff",
                border: `2px solid ${color}`,
                transition: "width .12s, height .12s",
              }}
            />
            {hover === i && (
              <Box
                style={{
                  position: "absolute",
                  bottom: "20px",
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
                sx={{
                  backgroundColor: "#2F2B3D",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  whiteSpace: "nowrap",
                  zIndex: 6,
                  pointerEvents: "none",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#fff", lineHeight: 1.3 }}>
                  {formatValue(c.value)}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "rgba(255, 255, 255, 0.75)", lineHeight: 1.3 }}>
                  {c.full}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ position: "relative", height: "14px", mt: "6px" }}>
        {coords.map((c, i) => (
          <Typography
            key={i}
            style={{
              position: "absolute",
              left: `${c.x}%`,
              transform: "translateX(-50%)",
            }}
            sx={{
              fontSize: "10px",
              color: "rgba(71, 84, 103, 0.85)",
              whiteSpace: "nowrap",
            }}
          >
            {c.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export default Sparkline;
